import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { EventsState } from '../../../../store/types';
import { getDefaultAgendaImage } from '../../../../store/utils/create-event';
import {
	LanguagesAbbr,
	RegistrationStepType,
	Session,
	SessionPreview,
	SessionTypesEnum,
	Speaker,
	SpeakerAttribute as SpeakerAttributeType
} from '../../../../types/working-model';
import { hasSessionAccess } from '../../../../utils/registration-utils';
import { EIcon } from '../../../general-ui/icon/icon';
import ModalComponent from '../../../general-ui/modal/modal';
import { renderSocialLinks } from '../../../general-ui/social-links/social-links';
import { Tag } from '../../../general-ui/tag/tag';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { ParamsProps } from '../../live-event';
import { eventPath, stylingOverrideReturns } from '../../utils';
import '../../../../scss/live-event/base/containers/speaker-details-modal.scss';
import { getTextValue } from '../../../../utils/utils';
import useTranslationRoute from '../../hooks/use-translation-route';
import { useTranslate } from '../../../../i18n/useTranslationModules';

interface Props {
	speaker: Speaker;
	template: string;
	language: LanguagesAbbr;
	stylingOverrides: stylingOverrideReturns;
	onClose: () => void;
	relatedSessions: number[];
	params: ParamsProps,
	onRelatedSessionClick: (path: string, state?: unknown) => void;
}
export const SpeakerDetailsModal = ({
	speaker,
	language,
	stylingOverrides,
	template,
	onClose,
	relatedSessions,
	params,
	onRelatedSessionClick
}: Props): JSX.Element => {

	const { moduleTitleOverrides, primaryButtonOverrides } = stylingOverrides;

	const [showMore, setShowMore] = useState(speaker?.description?.base?.length < 250); // only display the show more button if the description length is greater than 250 chars

	const { description } = getTextValue(speaker, language);
	const translationRoute = useTranslationRoute();
	const { t } = useTranslate(translationRoute);
	const moduleId = speaker.module_id;

	const socialLinks = speaker.link_social ? renderSocialLinks(speaker.social_links || [], primaryButtonOverrides) : null;

	const speakerAttributes = speaker?.attributes ? (
		speaker?.attributes.map((attribute, idx) => (
			<SpeakerAttribute key={idx} attribute={attribute} template={template} language={language} />
		))) : (
		null
	);

	return (
		<ModalComponent
			className={classNames(
				'ev-modal-wrapper live-event-container person-details-modal',
				template
			)}
			title=""
			open={true}
			onRequestClose={onClose}
			closeable
			cancellable
		>
			<div className="person-details-modal-content">
				<div className="person-details-image">
					<img src={speaker.image} alt={speaker.first_name} />
				</div>

				<div className="person-details-content">
					<TypographyItem
						className={classNames('person-details-name stable', moduleTitleOverrides)}
						role="heading"
						aria-level={3}
					>
						{t(`speakers.${moduleId}.first_name`, speaker.first_name)} {t(`speakers.${moduleId}.last_name`, speaker.last_name)}
						<Tag text="Speaker" classNameTag="tag-container-speaker" />
					</TypographyItem>
					<TypographyItem className={classNames('evt-body-text-2 person-details-position')} tagName="p">
						{t(`speakers.${moduleId}.job_title`, speaker.job_title?.[language] as string ?? speaker.job_title?.base ?? '')}
					</TypographyItem>
					{socialLinks}
					<TypographyItem className={classNames('evt-body-text-2 person-details-description', !showMore ? 'reduced' : '')} tagName="p">
						{t(`speakers.${moduleId}.description`, description)}
					</TypographyItem>
					{showMore ? (
						<></>
					) : (
						<div className={classNames('person-details-description-show-more')} onClick={() => setShowMore(!showMore)}>
							<button className={classNames('secondary evt-button', template)}>Show more</button>
						</div>
					)}

					<ul className="personal-data-list">
						{speakerAttributes}
					</ul>
				</div>

				<SessionList template={template} relatedSessions={relatedSessions} params={params} onRelatedSessionClick={onRelatedSessionClick} />
			</div>
		</ModalComponent>
	);
};

interface SessionListProps {
	template: string;
	relatedSessions: number[];
	params: ParamsProps;
	onRelatedSessionClick: (path: string, state?: unknown) => void;
}
const SessionList = ({ template, relatedSessions, params, onRelatedSessionClick }: SessionListProps): JSX.Element => {
	return (
		<div className="person-details-sessions">
			<div className="person-details-sessions-header">
				{!!relatedSessions.length && (
					<TypographyItem
						className="session-label stable"
						tagName="h4"
					>
						Sessions ({relatedSessions.length})
					</TypographyItem>
				)}
			</div>
			<ul>
				{
					relatedSessions
						? relatedSessions.map((id, idx) => (
							<SessionItem
								key={id}
								template={template}
								sessionId={id}
								params={params}
								onRelatedSessionClick={onRelatedSessionClick}
								idx={idx}
							/>
						))
						: null
				}
			</ul>
		</div>
	);
};

interface SessionItemProps {
	template: string;
	sessionId: number;
	params: ParamsProps;
	onRelatedSessionClick: (path: string, state?: unknown) => void
	idx: number;
}

const SessionItem = ({ template, sessionId, params, onRelatedSessionClick, idx }: SessionItemProps): JSX.Element => {
	const {
		LiveEventReducer: {
			eventBundle,
			registrationId,
			validSessions,
			paidSessions
		}
	} = useSelector((state: EventsState) => state);

	const { language, eventName, uuid } = params;

	const availableSessions = eventBundle?.sessions ?? eventBundle?.sessionsPreviews;

	const session = useMemo(() => {
		if (availableSessions) {
			//ts was complaining about this union, couldn't figure out why, it should work just fine.
			return (availableSessions as any[]).find((session: Session | SessionPreview) => session.session === sessionId);
		}
	}, [availableSessions, sessionId]);

	const sessionLink = useMemo(() => {
		if (!session) return "";
		const ticketingStep = eventBundle?.registration_steps?.find(step => step.type === RegistrationStepType.ticketing && step.isOn);
		let sessionHasTicket = false;

		if (ticketingStep) {
			const sessionTickets = ticketingStep.ticketing?.find(ticket => ticket.session_uuids.includes(session.uuid));
			if (sessionTickets) {
				sessionHasTicket = true;
			}
		}

		// if user has access (or if open event)
		if (hasSessionAccess(eventBundle, session, validSessions, paidSessions)) {
			let baseUrl = `/${eventPath(eventName, uuid)}/${language}/registration`;
			// if no ticketing or ticketing and user has access to session
			if (!ticketingStep || (ticketingStep && sessionHasTicket)) {
				baseUrl = `/${eventPath(eventName, uuid)}/${language}/session/${session.uuid}`;
			}

			return baseUrl;
		} else {
			// user doesn't have access so we don't provide a link
			return "";
		}
	}, [session, eventBundle, validSessions, paidSessions, eventName, uuid, language]);

	const handleSessionClick = () => {
		if (eventBundle?.registration_on && !registrationId) {
			onRelatedSessionClick(`/${eventPath(eventName, uuid)}/${language}/registration`);
		} else {
			if (!sessionLink) return null;
			onRelatedSessionClick(sessionLink);
		}
	};

	return (
		<li className="session-item">
			<div className="session-item-image">
				{session?.image ?
					<img src={session.image} width="88" height="88" aria-hidden="true" />
					:
					<img src={getDefaultAgendaImage(template, idx)} width="88" height="88" aria-hidden="true" />
				}
			</div>
			<div className="session-item-content">
				<TypographyItem
					className="session-item-date stable"
					tagName="time"
				>
					{session && session.session_type === SessionTypesEnum.onDemand && session.timestamp && new Date(session.timestamp).toLocaleDateString()}
				</TypographyItem>
				<TypographyItem
					className={classNames('session-item-title text-ellipsis evt-heading-5 stable')} tagName="h4">
					{session?.title[language] || session?.title.base}
				</TypographyItem>
				<button disabled={!sessionLink} className="session-item-link no-style no-padding" onClick={handleSessionClick}>Session details</button>
			</div>
		</li>
	);
};

interface ISpeakerAttribute {
	template: string;
	attribute: SpeakerAttributeType;
	language: LanguagesAbbr | 'base';
}

const SpeakerAttribute: React.FC<ISpeakerAttribute> = ({ attribute, language }) => {
	const { icon, attribute: attributeTitle, value } = attribute;
	return (
		<li>
			<div className="personal-data-list-icon">
				<EIcon name={icon} />
			</div>
			<div className="personal-data-list-content">
				<TypographyItem className="personal-data-list-label">
					{attributeTitle?.[language] ?? attributeTitle.base}
				</TypographyItem>
				<TypographyItem className={classNames('evt-body-text-2 personal-data-list-value')}>
					{value?.[language] ?? value.base}
				</TypographyItem>
			</div>
		</li>
	);
};
